import { extendTheme } from "@chakra-ui/react";

// ─── EDIT THESE ────────────────────────────────────────────────────────────────

const DARK = {
  bg: "#1a1209",
  text: "#e8d9c4",
  dim: "#bead96",
  accent: "#c8a97e",
  surface: "#241910", // lifted panel — distinct from page bg
  border: "rgba(255,255,255,0.07)",
};

const LIGHT = {
  bg: "#f5ede0",
  text: "#1a0c04",
  dim: "#6a4020",
  accent: "#6a4020",
  surface: "#fdfaf5",
  border: "rgba(0,0,0,0.08)",
};

const FONTS = {
  heading: `'DM Serif Display', Georgia, serif`,
  body: `'DM Sans', system-ui, sans-serif`,
};

// Brand scale, so components that take a `colorScheme` have somewhere on-brand to point.
// Chakra's default is `blue`, which is where the stray blue focus rings and info toasts came from.
const COFFEE = {
  50: "#f7f0e8",
  100: "#e6d5c3",
  200: "#d9bfa2",
  300: "#c8a97e",
  400: "#a9814f",
  500: "#8b4513",
  600: "#6a4020",
  700: "#4f2f18",
  800: "#2c1d11",
  900: "#1d1308",
};

// Tinted washes reused by every hover/pressed state, so one hover can't drift from another.
const WASH = {
  dark: { soft: "rgba(200,169,126,0.09)", strong: "rgba(200,169,126,0.16)" },
  light: { soft: "rgba(139,62,18,0.07)", strong: "rgba(139,62,18,0.12)" },
};
const wash = (colorMode) => (colorMode === "dark" ? WASH.dark : WASH.light);

// One palette and one type ramp for every page's chrome. Pages import these instead of
// re-declaring hex values and font sizes, which is how Home/About and Contact/Privacy/
// Collections ended up on two different scales.
export const TONE = { light: LIGHT, dark: DARK };
// Owner-verified tick. The one non-coffee colour, on purpose: it has to read as "verified" at a glance.
export const VERIFIED_BLUE = "#1d9bf0";

export const TYPE = {
  hero: { base: "34px", sm: "48px", lg: "72px" }, // homepage only
  pageTitle: { base: "32px", md: "48px" },        // h1 on every interior page
  sectionTitle: { base: "24px", md: "32px" },     // h2 within a page
  blockTitle: { base: "20px", md: "24px" },       // h3 / card + policy-section headings
  lead: { base: "17px", md: "20px" },             // the line under an h1
  body: "16px",                                   // paragraphs
  meta: "14px",                                   // captions, footnotes, nav
};

// ──────────────────────────────────────────────────────────────────────────────

const theme = extendTheme({
  fonts: FONTS,

  colors: { coffee: COFFEE },

  shadows: {
    outline: `0 0 0 3px rgba(200,169,126,0.55)`,
  },

  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },

  styles: {
    global: ({ colorMode }) => {
      const t = colorMode === "dark" ? DARK : LIGHT;
      return {
        body: {
          bg: t.bg,
          color: t.text,
          fontFamily: FONTS.body,
          minHeight: "100vh",
          "@supports (min-height: 100dvh)": { minHeight: "100dvh" },
        },
        "h1, h2, h3, h4, h5, h6": { fontFamily: FONTS.heading, fontWeight: 400 },
        "a:focus-visible, button:focus-visible, [tabindex]:focus-visible": { outline: "2px solid currentColor", outlineOffset: "3px" },
        "@media (prefers-reduced-motion: reduce)": { "*, *::before, *::after": { animationDuration: "0.01ms !important", animationIterationCount: "1 !important", transitionDuration: "0.01ms !important", scrollBehavior: "auto !important" } },
      };
    },
  },

  components: {
    Heading: {
      baseStyle: { fontFamily: "heading", fontWeight: 400 },
    },
    Modal: {
      baseStyle: { header: { fontFamily: "heading", fontWeight: 400 } },
    },
    Button: {
      baseStyle: {
        fontFamily: "body",
        borderRadius: "full",
        fontWeight: 500,
        _focusVisible: { outline: "2px solid", outlineColor: "currentColor", outlineOffset: "3px" },
      },
      variants: {
        solid: ({ colorMode }) => ({
          bg: colorMode === "dark" ? DARK.accent : LIGHT.accent,
          color: colorMode === "dark" ? DARK.surface : "#fff",
          _hover: {
            bg: colorMode === "dark" ? "#d4b98e" : "#5a3518",
            transform: "translateY(-1px)",
          },
        }),
        ghost: ({ colorMode }) => ({
          color: colorMode === "dark" ? DARK.dim : LIGHT.dim,
          _hover: {
            bg: wash(colorMode).soft,
            color: colorMode === "dark" ? DARK.accent : LIGHT.accent,
          },
          _active: { bg: wash(colorMode).strong },
        }),
        // Chakra's default outline is a grey border that reads as foreign on the cream page.
        // Same tokens as FindNearMeButton so the two outline buttons on the homepage agree.
        outline: ({ colorMode }) => ({
          color: colorMode === "dark" ? DARK.accent : LIGHT.accent,
          border: "1px solid",
          borderColor:
            colorMode === "dark"
              ? "rgba(200,169,126,0.32)"
              : "rgba(139,62,18,0.28)",
          bg:
            colorMode === "dark"
              ? "rgba(200,169,126,0.05)"
              : "rgba(139,62,18,0.04)",
          _hover: {
            bg:
              colorMode === "dark"
                ? "rgba(200,169,126,0.11)"
                : "rgba(139,62,18,0.09)",
            borderColor:
              colorMode === "dark"
                ? "rgba(200,169,126,0.52)"
                : "rgba(139,62,18,0.48)",
          },
        }),
      },
      defaultProps: { variant: "solid" },
    },

    IconButton: {
      variants: {
        ghost: ({ colorMode }) => ({
          color: colorMode === "dark" ? DARK.dim : LIGHT.dim,
          _hover: {
            bg: wash(colorMode).soft,
            color: colorMode === "dark" ? DARK.accent : LIGHT.accent,
          },
          _active: { bg: wash(colorMode).strong },
        }),
      },
    },

    // Untouched, Chakra hovers MenuItem to gray.100 / whiteAlpha.100 and rings it blue.
    Menu: {
      baseStyle: ({ colorMode }) => ({
        list: {
          bg: colorMode === "dark" ? DARK.surface : LIGHT.surface,
          border: "1px solid",
          borderColor: colorMode === "dark" ? DARK.border : LIGHT.border,
          borderRadius: "14px",
          py: 2,
          boxShadow:
            colorMode === "dark"
              ? "0 18px 44px rgba(0,0,0,0.5)"
              : "0 18px 44px rgba(60,30,10,0.16)",
        },
        item: {
          // Chakra drives every MenuItem state off a --menu-bg custom property that defaults to
          // gray.100 / whiteAlpha.100. Overriding only `bg` leaves that grey token underneath,
          // so point the variable itself at the brand wash and the states follow.
          "--menu-bg": wash(colorMode).soft,
          bg: "transparent",
          color: colorMode === "dark" ? DARK.text : LIGHT.text,
          fontFamily: FONTS.body,
          fontSize: "15px",
          minH: "44px",
          px: 4,
          _hover: { bg: wash(colorMode).soft, color: colorMode === "dark" ? DARK.accent : LIGHT.accent },
          _focus: { bg: wash(colorMode).soft, color: colorMode === "dark" ? DARK.accent : LIGHT.accent },
          _active: { "--menu-bg": wash(colorMode).strong, bg: wash(colorMode).strong },
          _expanded: { "--menu-bg": wash(colorMode).soft, bg: wash(colorMode).soft },
        },
      }),
    },

    // The homepage FAQ. Default hover is blackAlpha.50 with a blue focus ring.
    Accordion: {
      baseStyle: ({ colorMode }) => ({
        container: { borderColor: colorMode === "dark" ? DARK.border : LIGHT.border },
        button: {
          fontFamily: FONTS.body,
          _hover: { bg: wash(colorMode).soft },
          _focusVisible: { boxShadow: "none", outline: "2px solid currentColor", outlineOffset: "2px" },
        },
      }),
    },

    CloseButton: {
      baseStyle: ({ colorMode }) => ({
        borderRadius: "full",
        color: colorMode === "dark" ? DARK.dim : LIGHT.dim,
        _hover: { bg: wash(colorMode).soft, color: colorMode === "dark" ? DARK.accent : LIGHT.accent },
        _active: { bg: wash(colorMode).strong },
      }),
    },

    Link: {
      baseStyle: ({ colorMode }) => ({
        _hover: { color: colorMode === "dark" ? DARK.accent : LIGHT.accent },
      }),
    },

    // status="info" resolves to colorScheme blue. Only info is remapped; success/warning/error
    // keep their semantic colours, since a green "copied" and a red failure are worth reading fast.
    Alert: {
      variants: {
        solid: ({ colorScheme }) => ({
          container: {
            // Only the blue (status="info") scheme is remapped. success/warning/error keep
            // their semantic colours — a green "copied" and a red failure are worth reading fast.
            bg: colorScheme === "blue" ? COFFEE[600] : `${colorScheme}.500`,
            color: "#fdfaf5",
          },
        }),
      },
    },

    Card: {
      baseStyle: ({ colorMode }) => ({
        container: {
          bg: colorMode === "dark" ? DARK.surface : LIGHT.surface,
          borderRadius: "20px",
          border: "1px solid",
          borderColor: colorMode === "dark" ? DARK.border : LIGHT.border,
        },
      }),
    },

    Textarea: {
      variants: {
        outline: ({ colorMode }) => ({
          bg: colorMode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.8)",
          borderRadius: "12px",
          borderColor: colorMode === "dark" ? DARK.border : LIGHT.border,
          color: colorMode === "dark" ? DARK.text : LIGHT.text,
          fontFamily: FONTS.body,
          fontSize: "14px",
          _placeholder: {
            color: colorMode === "dark" ? DARK.dim : LIGHT.dim,
          },
          _focus: {
            borderColor: colorMode === "dark" ? DARK.accent : LIGHT.accent,
            boxShadow: "none",
          },
        }),
      },
      defaultProps: { variant: "outline" },
    },

    Tooltip: {
      baseStyle: ({ colorMode }) => ({
        bg: colorMode === "dark" ? DARK.accent : LIGHT.text,
        color: colorMode === "dark" ? DARK.surface : "#fdfaf5",
        "--popper-arrow-bg": colorMode === "dark" ? DARK.accent : LIGHT.text,
        borderRadius: "6px",
        fontSize: "12px",
        px: "8px",
        py: "5px",
      }),
    },

    Input: {
      variants: {
        outline: ({ colorMode }) => ({
          field: {
            bg:
              colorMode === "dark"
                ? "rgba(255,255,255,0.04)"
                : "rgba(255,255,255,0.8)",
            borderRadius: "full",
            borderColor: colorMode === "dark" ? DARK.border : LIGHT.border,
            color: colorMode === "dark" ? DARK.text : LIGHT.text,
            fontFamily: FONTS.body,
            fontSize: "14px",
            _placeholder: {
              color: colorMode === "dark" ? DARK.dim : LIGHT.dim,
            },
            _focus: {
              borderColor: colorMode === "dark" ? DARK.accent : LIGHT.accent,
              boxShadow: "none",
            },
          },
        }),
      },
      defaultProps: { variant: "outline" },
    },
  },
});

export default theme;
